/**
 * Phase 5D — Multi-currency checkout picker.
 *
 * Renders three pills (AUD · USD · KES) above the "Pay Now" button.
 * Auto-detects student country (browser timezone fallback) and pre-selects
 * KES for AF/EA timezones, USD for Americas, AUD otherwise. Student can
 * always override.
 *
 * Props:
 *   amountAudCents : number  — what AUD price to convert
 *   onConfirm({ preferred_currency }) : promise — caller initiates Paystack
 *   onClose()
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CreditCard, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const COUNTRY_TO_CURRENCY = {
  // KES — Kenya + neighbouring East Africa
  Africa: 'KES',
  // USD — Americas + Asia mostly
  America: 'USD',
  Pacific: 'USD',
  Asia: 'USD',
  // AUD — Australia / NZ default
  Australia: 'AUD',
};

const detectDefaultCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const region = tz.split('/')[0];
    // Kenya timezone specifically
    if (tz === 'Africa/Nairobi' || tz.startsWith('Africa/')) return 'KES';
    if (region === 'Australia') return 'AUD';
    return COUNTRY_TO_CURRENCY[region] || 'AUD';
  } catch { return 'AUD'; }
};

const CURRENCY_META = {
  AUD: { label: 'AUD', flag: 'AU', desc: 'Australian Dollar' },
  USD: { label: 'USD', flag: 'US', desc: 'US Dollar' },
  KES: { label: 'KES', flag: 'KE', desc: 'Kenyan Shilling' },
};

const PaymentCurrencyModal = ({ amountAudCents, label = 'Pay for class', onConfirm, onClose }) => {
  const [pricing, setPricing] = useState(null);
  const [currency, setCurrency] = useState(detectDefaultCurrency());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    _ax.get('/api/public/pricing')
      .then((r) => {
        setPricing(r.data || {});
        const enabled = r.data?.paystack_enabled_currencies || ['USD'];
        // If user's auto-detected currency isn't Paystack-enabled, snap to the
        // first enabled currency (USD until Paystack adds AUD/KES).
        setCurrency((cur) => (enabled.includes(cur) ? cur : enabled[0]));
      })
      .catch(() => toast.error('Could not load FX rates'));
  }, []);

  const supported = pricing?.supported_checkout_currencies || ['AUD', 'USD', 'KES'];
  const enabled = pricing?.paystack_enabled_currencies || ['USD'];
  const blockedMessage = pricing?.paystack_blocked_message
    || 'Selected currency is not enabled for Paystack yet. Please choose USD.';
  const fxToUsd = pricing?.fx_aud_to_usd ?? 0.65;
  const fxToKes = pricing?.fx_aud_to_kes ?? 86.5;
  const aud = (amountAudCents || 0) / 100;

  const preview = {
    AUD: { amount: aud, rate: 1 },
    USD: { amount: aud * fxToUsd, rate: fxToUsd },
    KES: { amount: aud * fxToKes, rate: fxToKes },
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm({ preferred_currency: currency });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="currency-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700" data-testid="currency-modal-close"><X className="h-4 w-4" /></button>
        <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
        <h3 className="text-lg font-bold text-gray-900 mt-1">{label}</h3>
        <p className="text-xs text-gray-500 mt-1">All prices are in AUD. Choose what currency you'd like to pay in — we'll charge that amount via Paystack.</p>

        <div className="mt-4 rounded-xl border border-gray-200 p-4 bg-gray-50">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Price</div>
          <div className="text-2xl font-bold text-gray-900" data-testid="currency-modal-aud-amount">AUD {aud.toFixed(2)}</div>
          <div className="text-[10px] text-gray-500 mt-1">Tutor earnings and Winhouse revenue always stay in AUD.</div>
        </div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Pay in</div>
          <div className="grid grid-cols-3 gap-2" data-testid="currency-pills">
            {supported.map((c) => {
              const meta = CURRENCY_META[c] || { label: c };
              const sel = currency === c;
              const isEnabled = enabled.includes(c);
              const p = preview[c] || { amount: aud, rate: 1 };
              return (
                <button key={c} onClick={() => isEnabled && setCurrency(c)} data-testid={`currency-pill-${c}`}
                  disabled={!isEnabled}
                  title={isEnabled ? '' : blockedMessage}
                  className={`rounded-xl border-2 p-3 text-left transition ${sel ? 'border-red-700 bg-red-50' : 'border-gray-200 hover:border-gray-300 bg-white'} ${isEnabled ? '' : 'opacity-50 cursor-not-allowed grayscale'}`}>
                  <div className="text-sm font-bold text-gray-900 flex items-center gap-1">
                    {meta.label}
                    {!isEnabled && <span className="text-[8px] uppercase tracking-wider text-amber-700 bg-amber-100 px-1 rounded">Soon</span>}
                  </div>
                  <div className="text-[10px] text-gray-500">{meta.desc}</div>
                  <div className="text-[11px] font-bold text-emerald-700 mt-1.5" data-testid={`currency-converted-${c}`}>
                    {c === 'AUD' ? `AUD ${p.amount.toFixed(2)}` : `${c} ${p.amount.toFixed(2)}`}
                  </div>
                  {c !== 'AUD' && (
                    <div className="text-[9px] text-gray-400 mt-0.5">@ {p.rate.toFixed(c === 'KES' ? 2 : 4)} /AUD</div>
                  )}
                </button>
              );
            })}
          </div>
          {enabled.length < supported.length && (
            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2" data-testid="currency-blocked-note">
              {blockedMessage}
            </div>
          )}
        </div>

        <button onClick={confirm} disabled={busy || !enabled.includes(currency)}
          className="mt-5 w-full h-11 rounded-full bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2"
          data-testid="currency-modal-confirm">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          Continue to Paystack ({currency} {preview[currency].amount.toFixed(2)})
        </button>
        <p className="text-[10px] text-gray-400 text-center mt-2">FX rate locked at checkout · safe & encrypted</p>
      </div>
    </div>
  );
};

export default PaymentCurrencyModal;
